/**
 * What a language picker prints for a locale code: the language's own name (its autonym,
 * the way the account menu and the SEO centre already name languages), and as a hint the
 * name in the reader's own language with the code, so "saksa" or "german" or "de" all
 * find "Deutsch".
 *
 * Every world language comes from `world-languages.ts`; a code it does not hold (a region
 * tag such as `en-GB`) is named by the runtime (`Intl.DisplayNames`), and a code nobody
 * can name is printed as itself, so a stored value never turns into an empty row.
 *
 * PURE apart from reading the runtime's language names.
 */
import { WORLD_LANGUAGES, WORLD_LANGUAGE_BY_CODE } from "./world-languages";

export interface LocaleChoice {
  value: string;
  label: string;
  hint: string;
}

function upperFirst(text: string): string {
  return text ? text.charAt(0).toLocaleUpperCase() + text.slice(1) : text;
}

/** The name of `code` in the language `inLocale`; null when the runtime cannot say. */
function displayName(code: string, inLocale: string): string | null {
  try {
    const name = new Intl.DisplayNames([inLocale], { type: "language", fallback: "none" }).of(code);
    return name && name.toLowerCase() !== code.toLowerCase() ? name : null;
  } catch {
    return null;
  }
}

/** The language's name in itself: "Eesti", "Deutsch", "Русский". The code when nobody knows it. */
export function localeAutonym(code: string): string {
  const known = WORLD_LANGUAGE_BY_CODE[code]?.native ?? displayName(code, code);
  return upperFirst(known ?? code.toUpperCase());
}

/**
 * The picker's rows. `codes` absent = every world language, sorted by its own name.
 * `current` is kept even when `codes` leaves it out, so a stored value stays visible.
 */
export function localeChoices(codes: readonly string[] | undefined, displayLocale: string, current = ""): LocaleChoice[] {
  const list = codes ? [...codes] : WORLD_LANGUAGES.map((l) => l.code);
  const value = current.trim();
  if (value && !list.includes(value)) list.unshift(value);
  const rows = list.map((code) => {
    const label = localeAutonym(code);
    const local = displayName(code, displayLocale);
    const english = WORLD_LANGUAGE_BY_CODE[code]?.english;
    // Words the label does not already say, so a search in the reader's language or in
    // English finds the row too.
    const extra = [local, english].filter((w): w is string => !!w && w.toLowerCase() !== label.toLowerCase());
    const hint = [...new Set(extra.map((w) => w.toLowerCase()))].map((w) => upperFirst(w));
    return { value: code, label, hint: [...hint, code.toUpperCase()].join(" · ") };
  });
  return codes ? rows : rows.sort((a, b) => a.label.localeCompare(b.label, displayLocale));
}
